import React, { useId } from "react";

interface FormFieldProps {
  label: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
  helper?: React.ReactNode;
  error?: React.ReactNode;
  layout?: "stacked" | "inline";
  children: React.ReactNode;
  className?: string;
}

/**
 * Wraps a control with its label, helper text, and error message.
 *
 * Pass an htmlFor to bind the label to a specific control, or let
 * FormField generate one (then pass `aria-labelledby` or wrap your
 * input inside the FormField).
 */
export const FormField: React.FC<FormFieldProps> = ({
  label,
  htmlFor,
  required,
  helper,
  error,
  layout = "stacked",
  children,
  className,
}) => {
  const reactId = useId();
  const id = htmlFor ?? reactId;
  const isInline = layout === "inline";

  return (
    <div
      className={className}
      style={{
        display: "flex",
        flexDirection: isInline ? "row" : "column",
        gap: isInline ? "var(--spacing-sm)" : "var(--spacing-2xs)",
        alignItems: isInline ? "center" : "stretch",
      }}
    >
      <label
        htmlFor={id}
        style={{
          fontSize: "var(--font-size-small)",
          color: "var(--color-fg-muted)",
          flexShrink: 0,
        }}
      >
        {label}
        {required && (
          <span aria-hidden style={{ color: "var(--color-error)" }}>
            {" *"}
          </span>
        )}
      </label>
      <div style={{ flex: isInline ? 1 : undefined }}>{children}</div>
      {(helper || error) && (
        <div
          style={{
            fontSize: "var(--font-size-small)",
            color: error ? "var(--color-error)" : "var(--color-fg-muted)",
          }}
        >
          {error ?? helper}
        </div>
      )}
    </div>
  );
};
